import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, Check, Clock, Scissors, Calendar as Cal } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatTime12h } from "@/lib/time";

function fmtDuration(m) {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

const STEPS = ["service", "datetime", "info", "done"];

export default function BookingDialog({
  open,
  onOpenChange,
  services = [],
  initialServiceId = null,
  initialAddon = false,
}) {
  const bookable = useMemo(() => services.filter((s) => !s.is_addon), [services]);
  const addon = useMemo(() => services.find((s) => s.is_addon), [services]);

  const [step, setStep] = useState(0);
  const [serviceId, setServiceId] = useState(null);
  const [withAddon, setWithAddon] = useState(false);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [slotLoading, setSlotLoading] = useState(false);
  const [closed, setClosed] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const selectedService = bookable.find((s) => s.id === serviceId);
  const duration = selectedService
    ? selectedService.duration_min + (withAddon ? addon?.duration_min || 0 : 0)
    : 0;
  const basePrice = selectedService
    ? selectedService.price + (withAddon ? addon?.price || 0 : 0)
    : 0;
  const selectedSlot = slots.find((s) => s.time === slot);
  const afterHours = !!selectedSlot?.after_hours;
  const price = basePrice + (afterHours ? 10 : 0);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setServiceId(initialServiceId);
      setWithAddon(initialAddon);
      setDate(null);
      setSlot(null);
      setSlots([]);
      setName("");
      setPhone("");
      setEmail("");
      setConfirmed(null);
      setStep(initialServiceId ? 1 : 0);
    }
  }, [open, initialServiceId, initialAddon]);

  // Fetch slots on date/service change
  useEffect(() => {
    async function load() {
      if (!date || !serviceId) return;
      setSlotLoading(true);
      setSlot(null);
      try {
        const dateStr = format(date, "yyyy-MM-dd");
        const { data } = await api.get("/availability", {
          params: {
            date: dateStr,
            service_id: serviceId,
            with_facial_hair: withAddon,
          },
        });
        setSlots(data.slots || []);
        setClosed(!!data.closed);
      } catch (e) {
        toast.error(formatApiError(e, "Could not load available times"));
        setSlots([]);
      } finally {
        setSlotLoading(false);
      }
    }
    load();
  }, [date, serviceId, withAddon]);

  const canNextFromDatetime = date && slot;
  const canSubmit = name.trim() && phone.trim() && email.trim();

  const disabledDates = (d) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    // Only Thu (4), Fri (5), Sat (6), Sun (0) are open. JS getDay: Sun=0..Sat=6
    const day = d.getDay();
    const openDays = new Set([0, 4, 5, 6]);
    return d < now || !openDays.has(day);
  };

  async function submit() {
    if (!canSubmit || !date || !slot || !serviceId) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: serviceId,
        with_facial_hair: withAddon,
        date: format(date, "yyyy-MM-dd"),
        time: slot,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
      });
      setConfirmed(data);
      setStep(3);
      toast.success("Booking confirmed");
    } catch (e) {
      toast.error(formatApiError(e, "Booking failed"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-2xl bg-stone-950 border border-stone-800 rounded-none text-stone-50 p-0 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <DialogHeader className="p-6 md:p-8 border-b border-stone-800">
          <DialogTitle
            data-testid="booking-title"
            className="font-display text-2xl md:text-3xl tracking-tight text-stone-50 font-normal"
          >
            {step === 3 ? (
              <>
                You're <span className="italic text-amber-500">booked</span>.
              </>
            ) : (
              <>
                Book your <span className="italic text-amber-500">chair</span>
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-stone-400 text-sm">
            {step === 3
              ? "Confirmation details are shown below."
              : `Step ${step + 1} of 3 · ${STEPS[step].toUpperCase()}`}
          </DialogDescription>
          {step < 3 && (
            <div className="mt-4 flex gap-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`h-[2px] flex-1 ${i <= step ? "bg-amber-500" : "bg-stone-800"}`}
                />
              ))}
            </div>
          )}
        </DialogHeader>

        <div className="p-6 md:p-8">
          {/* STEP 0: Service */}
          {step === 0 && (
            <div data-testid="step-service" className="space-y-3">
              {bookable.map((s) => {
                const active = serviceId === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`select-service-${s.id}`}
                    onClick={() => setServiceId(s.id)}
                    className={`w-full text-left border p-5 flex items-center justify-between transition-colors duration-300 ${
                      active
                        ? "border-amber-500 bg-amber-500/5"
                        : "border-stone-800 hover:border-stone-700 bg-stone-900/50"
                    }`}
                  >
                    <div>
                      <div className="font-display text-xl text-stone-50">
                        {s.name}
                      </div>
                      <div className="text-xs text-stone-500 mt-1 flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {fmtDuration(s.duration_min)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-2xl text-amber-500">
                        ${s.price}
                      </div>
                    </div>
                  </button>
                );
              })}

              {serviceId && !bookable.find((s) => s.id === serviceId)?.is_addon && addon && (
                <label
                  data-testid="toggle-addon"
                  className="flex items-start gap-3 border border-stone-800 bg-stone-900/60 p-5 cursor-pointer hover:border-stone-700 transition-colors duration-300"
                >
                  <Checkbox
                    checked={withAddon}
                    onCheckedChange={(v) => setWithAddon(!!v)}
                    className="mt-1 border-amber-500 data-[state=checked]:bg-amber-500 data-[state=checked]:text-stone-950 rounded-none"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-display text-lg text-stone-50">
                        Add {addon.name}
                      </div>
                      <div className="text-amber-500 font-display">
                        +${addon.price}
                      </div>
                    </div>
                    <div className="text-xs text-stone-500 mt-1">
                      +{fmtDuration(addon.duration_min)} · {addon.description}
                    </div>
                  </div>
                </label>
              )}

              <div className="pt-2 flex justify-end">
                <Button
                  data-testid="step-service-next"
                  disabled={!serviceId}
                  onClick={() => setStep(1)}
                  className="bg-amber-500 text-stone-950 hover:bg-amber-400 rounded-none px-6 py-3 text-xs tracking-[0.2em] uppercase font-medium"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* STEP 1: Date & Time */}
          {step === 1 && (
            <div data-testid="step-datetime" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase text-amber-500/80 flex items-center gap-2 mb-3">
                    <Cal size={12} /> Pick a date
                  </Label>
                  <div className="border border-stone-800 bg-stone-900/60 p-2">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={disabledDates}
                      className="rounded-none"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase text-amber-500/80 flex items-center gap-2 mb-3">
                    <Clock size={12} /> Available times
                  </Label>
                  <div className="border border-stone-800 bg-stone-900/60 p-4 min-h-[300px]">
                    {!date && (
                      <div className="text-stone-500 text-sm">
                        Choose a date to see open slots.
                      </div>
                    )}
                    {date && slotLoading && (
                      <div className="text-stone-400 flex items-center gap-2 text-sm">
                        <Loader2 size={14} className="animate-spin" /> Finding open slots…
                      </div>
                    )}
                    {date && !slotLoading && closed && (
                      <div className="text-stone-400 text-sm">
                        Closed that day. Please pick another.
                      </div>
                    )}
                    {date && !slotLoading && !closed && slots.length === 0 && (
                      <div className="text-stone-400 text-sm">
                        No open slots for this date. Try another day.
                      </div>
                    )}
                    {date && !slotLoading && slots.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {slots.map((s) => {
                          const active = slot === s.time;
                          return (
                            <button
                              key={s.time}
                              data-testid={`slot-${s.time}`}
                              onClick={() => setSlot(s.time)}
                              className={`relative px-3 py-2 text-sm border transition-colors duration-300 ${
                                active
                                  ? "border-amber-500 bg-amber-500 text-stone-950"
                                  : "border-stone-800 text-stone-300 hover:border-amber-500/50 hover:text-amber-500"
                              }`}
                            >
                              {formatTime12h(s.time)}
                              {s.after_hours && (
                                <span
                                  className={`absolute -top-2 -right-2 text-[9px] tracking-widest px-1 py-[1px] border ${
                                    active
                                      ? "border-stone-950 bg-stone-950 text-amber-500"
                                      : "border-amber-500/60 bg-stone-950 text-amber-500"
                                  }`}
                                >
                                  +$10
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {date && !slotLoading && slots.length > 0 && slots.some((s) => s.after_hours) && (
                      <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-stone-500">
                        Late slots (9pm+) include a +$10 after-hours fee
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center pt-2">
                <div className="text-xs text-stone-500">
                  {selectedService && (
                    <>
                      {selectedService.name}
                      {withAddon ? " + Beard grooming" : ""} ·{" "}
                      {fmtDuration(duration)} · ${price}
                    </>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    data-testid="step-datetime-back"
                    onClick={() => setStep(0)}
                    className="rounded-none border-stone-700 text-stone-300 hover:bg-stone-900 hover:text-stone-50 bg-transparent"
                  >
                    Back
                  </Button>
                  <Button
                    data-testid="step-datetime-next"
                    disabled={!canNextFromDatetime}
                    onClick={() => setStep(2)}
                    className="bg-amber-500 text-stone-950 hover:bg-amber-400 rounded-none px-6 py-3 text-xs tracking-[0.2em] uppercase font-medium"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Contact info */}
          {step === 2 && (
            <div data-testid="step-info" className="space-y-5">
              <div>
                <Label className="text-[10px] tracking-[0.3em] uppercase text-stone-400">
                  Your name
                </Label>
                <Input
                  data-testid="input-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="mt-2 bg-stone-900 border-stone-800 rounded-none focus-visible:ring-1 focus-visible:ring-amber-500 focus-visible:border-amber-500 text-stone-50 placeholder:text-stone-600"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase text-stone-400">
                    Phone
                  </Label>
                  <Input
                    data-testid="input-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                    className="mt-2 bg-stone-900 border-stone-800 rounded-none focus-visible:ring-1 focus-visible:ring-amber-500 focus-visible:border-amber-500 text-stone-50 placeholder:text-stone-600"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase text-stone-400">
                    Email
                  </Label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 bg-stone-900 border-stone-800 rounded-none focus-visible:ring-1 focus-visible:ring-amber-500 focus-visible:border-amber-500 text-stone-50 placeholder:text-stone-600"
                  />
                </div>
              </div>

              <div className="border border-stone-800 bg-stone-900/60 p-5 mt-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-500/80 mb-3">
                  Summary
                </div>
                <div className="text-sm text-stone-300 space-y-1">
                  <div className="flex justify-between">
                    <span>Service</span>
                    <span className="text-stone-50">
                      {selectedService?.name}
                      {withAddon ? " + Beard grooming" : ""}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>When</span>
                    <span className="text-stone-50">
                      {date ? format(date, "EEE, MMM d") : ""} · {formatTime12h(slot)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Duration</span>
                    <span className="text-stone-50">{fmtDuration(duration)}</span>
                  </div>
                  {afterHours && (
                    <div className="flex justify-between">
                      <span>After-hours fee</span>
                      <span className="text-amber-500">+$10</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base pt-2 mt-2 border-t border-stone-800">
                    <span className="text-stone-400">Total (pay in shop)</span>
                    <span className="font-display text-amber-500 text-xl">
                      ${price}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button
                  variant="outline"
                  data-testid="step-info-back"
                  onClick={() => setStep(1)}
                  className="rounded-none border-stone-700 text-stone-300 hover:bg-stone-900 hover:text-stone-50 bg-transparent"
                >
                  Back
                </Button>
                <Button
                  data-testid="submit-booking"
                  disabled={!canSubmit || submitting}
                  onClick={submit}
                  className="bg-amber-500 text-stone-950 hover:bg-amber-400 rounded-none px-8 py-3 text-xs tracking-[0.2em] uppercase font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin mr-2" />
                      Booking…
                    </>
                  ) : (
                    <>Confirm Booking</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Confirmation */}
          {step === 3 && confirmed && (
            <div data-testid="step-confirmed" className="text-center py-4">
              <div className="mx-auto w-16 h-16 border border-amber-500 flex items-center justify-center text-amber-500 mb-6">
                <Check size={28} />
              </div>
              <h3 className="font-display text-3xl text-stone-50">
                See you, <span className="italic text-amber-500">{confirmed.name}</span>
              </h3>
              <p className="text-stone-400 mt-3 text-sm">
                Your appointment with Andaz is locked in. A copy of these
                details is safely saved.
              </p>
              <div className="mt-8 max-w-md mx-auto border border-stone-800 bg-stone-900/60 p-6 text-left space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-stone-500">Service</span>
                  <span className="text-stone-50">{confirmed.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Date</span>
                  <span className="text-stone-50">{confirmed.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Time</span>
                  <span className="text-stone-50">{formatTime12h(confirmed.time)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Duration</span>
                  <span className="text-stone-50">{fmtDuration(confirmed.duration_min)}</span>
                </div>
                <div className="flex justify-between pt-3 mt-3 border-t border-stone-800">
                  <span className="text-stone-400">Total (pay in shop)</span>
                  <span className="font-display text-amber-500 text-xl">
                    ${confirmed.total_price}
                  </span>
                </div>
              </div>
              <div className="mt-8 text-xs text-stone-500 flex items-center justify-center gap-2">
                <Scissors size={12} className="text-amber-500" />
                Reach out on IG or by phone if you need to reschedule.
              </div>
              <Button
                data-testid="close-confirmation"
                onClick={() => onOpenChange(false)}
                className="mt-8 bg-amber-500 text-stone-950 hover:bg-amber-400 rounded-none px-8 py-3 text-xs tracking-[0.2em] uppercase font-medium"
              >
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
